import * as React from 'react';
import { addImageParams, AddImageParamTypes, getAdQuestionUrl, getCatalogUrl } from '@/utils/urls';
import { Caption, Secondary } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { catalogCardDate } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalog.actions';
import { FormattedMessage } from 'react-intl';
import { getCatalogAd } from '@/redux/modules/catalogAd/catalogAd.selectors';
import { safelyEscapeText } from '@/utils/safelyRender';
import { trackAdUnitClick, trackAdUnitVisible } from '@/redux/modules/analytics';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import HoverIntent from '@liveauctioneers/caterwaul-components/lib/HoverIntent/HoverIntent';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback2';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import noop from '@/utils/noop';
import Slider from 'react-slick/lib/slider';
import styled from 'styled-components';

const sliderSettings = {
    arrows: false,
    autoplay: true,
    autoplaySpeed: 5000,
    dots: false,
    easing: 'ease-in-out',
    infinite: true,
    slidesToScroll: 1,
    slidesToShow: 1,
};

const imageParams: AddImageParamTypes = {
    height: 234,
};

type Props = {
    adId: string;
    className?: string;
    closeNav: () => void;
};

const CategoryNavigationAd = ({ adId, className, closeNav }: Props) => {
    const dispatch = useAppDispatch();
    const {
        adUnitId,
        catalogId,
        catalogTitle,
        destination,
        imageUrl1,
        imageUrl2,
        imageUrl3,
        saleStartTs,
        sellerId,
        sellerName,
    } = useAppSelector((state) => getCatalogAd(state, { categoryIds: adId, pageName: 'navigation' }));

    // If only the second image exists we're going to treat it really special, it will get to be the only image.
    const customImage = imageUrl2 && !imageUrl1 && !imageUrl3;

    // we want to make sure that we only use truthy imageUrls so we filter;
    const images = [imageUrl1, imageUrl2, imageUrl3].filter((i) => Boolean(i));

    React.useEffect(() => {
        Boolean(adUnitId) &&
            dispatch(trackAdUnitVisible({ adUnitId, catalogId, consignment: false, page: 'navigation', sellerId }));
    }, [adUnitId, catalogId, dispatch, sellerId]);

    const trackClick = React.useCallback(() => {
        Boolean(adUnitId) &&
            dispatch(trackAdUnitClick({ adUnitId, catalogId, consignment: false, page: 'navigation', sellerId }));
    }, [dispatch, adUnitId, catalogId, sellerId]);

    const mapImages = React.useCallback(
        (url: string) => (
            <StyledImageContainer key={`at-ad-${url}`}>
                <ImageWithFallback
                    placeholder={addImageParams({ ...imageParams, quality: 15, url })}
                    src={addImageParams({ ...imageParams, url })}
                />
            </StyledImageContainer>
        ),
        []
    );

    const loadCatalog = React.useCallback(() => {
        dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] }));
    }, [catalogId, dispatch]);

    if (!Boolean(adUnitId)) {
        return null;
    }

    return (
        <HoverIntent interval={50} onMouseOut={noop} onMouseOver={loadCatalog} sensitivity={5} timeout={50}>
            <StyledContainer onClick={trackClick}>
                {customImage ? (
                    <StyledCustomImageLink
                        className={className}
                        onClick={closeNav}
                        route={!destination.startsWith('http')}
                        to={destination || getCatalogUrl(catalogId, catalogTitle)}
                    >
                        <StyledCustomImage
                            alt={catalogTitle}
                            placeholder={addImageParams({ ...imageParams, quality: 15, url: images[0] })}
                            src={addImageParams({ ...imageParams, url: images[0] })}
                        />
                    </StyledCustomImageLink>
                ) : (
                    <StyledCategoryNavigationAd
                        className={className}
                        onClick={closeNav}
                        route={!destination.startsWith('http')}
                        to={destination || getCatalogUrl(catalogId, catalogTitle)}
                    >
                        <StyledImagesContainer>
                            {images.length > 1 ? (
                                <Slider {...sliderSettings}>{images.map(mapImages)}</Slider>
                            ) : (
                                <ImageWithFallback
                                    alt={catalogTitle}
                                    placeholder={addImageParams({ ...imageParams, quality: 15, url: images[0] })}
                                    src={addImageParams({ ...imageParams, url: images[0] })}
                                />
                            )}
                        </StyledImagesContainer>
                        <StyledCopyContainer>
                            <StyledTextContainer>
                                <StyledAuctionName>{safelyEscapeText(catalogTitle)}</StyledAuctionName>
                            </StyledTextContainer>
                            <StyledTextContainer>
                                <StyledAuctionDate>{catalogCardDate(saleStartTs)}</StyledAuctionDate>
                            </StyledTextContainer>
                            <StyledTextContainer>
                                <StyledAuctioneerName>{safelyEscapeText(sellerName)}</StyledAuctioneerName>
                            </StyledTextContainer>
                        </StyledCopyContainer>
                    </StyledCategoryNavigationAd>
                )}
                <StyledAdInfoContainer>
                    <FormattedMessage id="advertisement" tagName="span" />
                    <Link target="_blank" to={getAdQuestionUrl()}>
                        <FormattedMessage id="your_ad_here" />
                    </Link>
                </StyledAdInfoContainer>
            </StyledContainer>
        </HoverIntent>
    );
};

export default CategoryNavigationAd;

const StyledContainer = styled.div`
    width: 266px;
    height: 388px;
    display: flex;
    flex-direction: column;
`;

const StyledCustomImage = styled(ImageWithFallback)`
    width: 266px;
    height: 351px;
    display: block;
`;

const StyledCustomImageLink = styled(Link)`
    width: 266px;
    height: 351px;
    display: block;
`;

const StyledCategoryNavigationAd = styled(Link)`
    width: 266px;
    height: 351px;
    text-decoration: none;
    position: relative;
    padding: 16px;
    border-left: 1px solid ${({ theme }) => theme.colors.grey400};
`;

const StyledImagesContainer = styled.div`
    width: 234px;
    height: 234px;
    margin-bottom: 8px;
    overflow: hidden;
`;

const StyledCopyContainer = styled.div`
    height: 86px;
    width: 234px;
    position: relative;
`;

const StyledTextContainer = styled.div`
    width: 100%;
    margin-bottom: 4px;
    &:last-child {
        margin-bottom: 0;
    }
`;

const StyledAuctionName = styled(Secondary)`
    color: ${({ theme }) => theme.colors.black};

    /* I think this works on all browsers */
    && {
        -webkit-line-clamp: 2;
        overflow: hidden;
        white-space: normal;

        /* stylelint-disable value-no-vendor-prefix  */
        /* stylelint-disable property-no-vendor-prefix */
        display: -webkit-box;
        -webkit-box-orient: vertical;
    }
`;
const StyledAuctionDate = styled(Secondary)`
    color: ${({ theme }) => theme.colors.grey100};
`;

const StyledAuctioneerName = styled(Caption)`
    color: ${({ theme }) => theme.colors.grey100};
`;

const StyledAdInfoContainer = styled.div`
    padding: 0 16px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 266px;
    height: 37px;
    border-left: 1px solid ${({ theme }) => theme.colors.grey400};
    border-top: 1px solid ${({ theme }) => theme.colors.grey400};
    font-size: 12px;

    & > span {
        color: ${({ theme }) => theme.colors.grey100};
        display: inline-block;
        margin-right: 4px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.pageWidthNum - 1}px) {
        border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
    }
`;

const StyledImageContainer = styled.div`
    /* have to do this !important to override the carousels styles */
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    overflow: hidden;
`;
